import { useEffect, useRef } from 'react'
import { useSnapshot } from 'valtio'
import { FtIcon } from './icons.tsx'
import { MessageBubble } from './messageBubble.tsx'
import { TUTOR_AVATAR } from '../../../shared/branding.ts'
import {
  CAPTION,
  FreeTalkStore,
  STATUS,
  WARN_RECORD_SEC,
  formatClock,
} from '../freeTalkStore.ts'

function minutesLabel(seconds: number) {
  const s = Math.round(seconds)
  const long =
    s <= 0 ? '0 мин' : s < 60 ? 'меньше минуты' : Math.floor(s / 60) + ' мин'
  const short = s > 0 && s < 60 ? '<1 мин' : long
  return { long, short }
}

export function TalkScreen(props: {
  store: FreeTalkStore
  onClose: () => void
}) {
  const { store } = props
  const state = useSnapshot(store.state)
  const chatRef = useRef<HTMLDivElement>(null)
  const phase = state.phase
  const mins = minutesLabel(state.secondsToday)
  const isRec = phase === 'rec'
  const timerWarn = state.recSeconds >= WARN_RECORD_SEC

  const msgCount = state.msgs.length
  useEffect(() => {
    const el = chatRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [msgCount, phase])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (state.roleOpen) store.closeRoleMenu()
      else if (state.hintOpen) store.closeHint()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
    }
  }, [state.roleOpen, state.hintOpen, store])

  return (
    <section className="ft-talk">
      <header className="ft-head">
        <button
          className="ft-pill"
          aria-haspopup="true"
          aria-expanded={state.roleOpen}
          onClick={(e) => {
            e.stopPropagation()
            store.toggleRoleMenu()
          }}
        >
          <FtIcon name="swap" size={18} />
          Сменить роль
        </button>
        <span className="ft-title">Свободное общение</span>
        <span className="ft-mins">
          <FtIcon name="clock" size={18} />
          <b className="ft-long">{mins.long}</b>
          <b className="ft-short">{mins.short}</b>
          <span className="ft-long">практики сегодня</span>
        </span>
        <button
          className="ft-iconbtn"
          aria-label="Завершить разговор"
          title="Завершить разговор"
          onClick={props.onClose}
        >
          <FtIcon name="x" size={20} />
        </button>

        {state.roleOpen && (
          <div
            className="ft-rolemenu"
            role="menu"
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <h4>С кем поговорим?</h4>
            <button
              className="ft-role cur"
              role="menuitemradio"
              aria-checked="true"
              onClick={() => {
                store.closeRoleMenu()
              }}
            >
              <span className="ft-ric" aria-hidden="true">
                💬
              </span>
              <span>
                <b>{state.role.name}</b>
                <span className="ft-rdesc">{state.role.description}</span>
              </span>
              <span className="ft-chk">
                <FtIcon name="check" size={20} stroke={2.6} />
              </span>
            </button>
            <p className="ft-soon">Другие роли появятся позже.</p>
          </div>
        )}
      </header>

      <div className="ft-body">
        <div className="ft-persona" aria-label="Собеседник">
          <img className={`ft-orb ${phase}`} src={TUTOR_AVATAR} alt="" />
          <div className="ft-pinfo">
            <div className="ft-pname">{state.role.name}</div>
            <p className="ft-pdesc">{state.role.description}</p>
            <div className="ft-prow">
              <span className="ft-status" aria-live="polite">
                <span
                  className="ft-dot"
                  style={{ background: STATUS[phase].color }}
                />
                <span>{STATUS[phase].text}</span>
              </span>
              <span className="ft-lvl">Уровень {state.level}</span>
            </div>
          </div>
        </div>

        <div className="ft-chatcard">
          <div
            className="ft-chat"
            ref={chatRef}
            aria-label="Разговор"
            aria-live="polite"
          >
            {state.startError && (
              <div className="ft-syserr">
                <FtIcon name="warn" size={18} />
                Не получилось начать разговор
                <button onClick={() => void store.startConversation()}>
                  Повторить
                </button>
              </div>
            )}
            {state.msgs.map((m) => (
              <MessageBubble
                key={m.id}
                m={m}
                store={store}
                playing={state.playingId === m.id}
                progress={state.playingId === m.id ? state.playProgress : 0}
                disabled={isRec}
              />
            ))}
          </div>

          <div className={`ft-ctrl ${isRec ? 'is-rec' : ''}`}>
            {state.hintOpen && (
              <div className="ft-hint">
                <button
                  className="ft-hintx"
                  aria-label="Закрыть подсказку"
                  onClick={() => {
                    store.closeHint()
                  }}
                >
                  <FtIcon name="x" size={18} />
                </button>
                <h4>Можно начать так</h4>
                <div className="ft-chips">
                  {state.role.hints.map((h) => (
                    <span key={h} className="ft-chip" lang="en">
                      {h}
                    </span>
                  ))}
                </div>
                <p>Не знаешь слово — скажи по-русски, я подскажу, как по-английски.</p>
              </div>
            )}
            <div className="ft-ctrlrow">
              <div className="ft-cleft">
                {isRec ? (
                  <button
                    className="ft-round"
                    aria-label="Отменить запись"
                    onClick={() => void store.cancelRecording()}
                  >
                    <FtIcon name="x" size={20} />
                  </button>
                ) : (
                  <button
                    className="ft-round"
                    aria-label="Подсказка"
                    aria-pressed={state.hintOpen}
                    onClick={() => {
                      store.toggleHint()
                    }}
                  >
                    <FtIcon name="bulb" size={20} />
                  </button>
                )}
              </div>
              <button
                className="ft-mic"
                disabled={phase === 'think' || !state.started}
                aria-label={
                  isRec
                    ? 'Отправить запись'
                    : phase === 'speak'
                    ? 'Перебить и ответить'
                    : 'Начать запись'
                }
                onClick={() => {
                  store.micPress()
                }}
              >
                <span className="ft-ring" />
                <span className="ft-ring r2" />
                <FtIcon name={isRec ? 'send' : 'mic'} size={30} stroke={2.2} />
              </button>
              <div className="ft-cright">
                {isRec && (
                  <span className={`ft-timer ${timerWarn ? 'warn' : ''}`}>
                    {formatClock(Math.floor(state.recSeconds))}
                  </span>
                )}
              </div>
            </div>
            <div className={`ft-caption ${state.captionFlash ? 'flash' : ''}`}>
              {state.caption || CAPTION[phase]}
            </div>
          </div>
        </div>
      </div>
      {state.roleOpen && (
        <div
          className="ft-scrim"
          onClick={() => {
            store.closeRoleMenu()
          }}
        />
      )}
    </section>
  )
}
